<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Card, SelectInput } from 'ui-kit'
import { Copy, ArrowLeft, Plus, Pencil } from 'lucide-vue-next'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import Modal from '@/components/shared/Modal.vue'
import ColorThemeEditor from '@/components/admin/ColorThemeEditor.vue'
import LogoUploader from '@/components/admin/LogoUploader.vue'
import { useEnvironment } from '@/composables/useEnvironment'
import { useSchools } from '@/composables/api/useSchoolsApi'
import {
  useColorThemes,
  useCreateColorTheme,
  useUpdateColorTheme,
  useAssignSchoolColorTheme,
  type SchoolColorThemeData,
} from '@/composables/api/useSchoolColorThemesApi'
import {
  useSchoolLogo,
  useUploadSchoolLogo,
} from '@/composables/api/useSchoolLogoApi'
import type { ServiceType } from '@/constants/environments'

interface Props {
  id: string
}
const props = defineProps<Props>()

const router = useRouter()
const { currentService } = useEnvironment()
const service = currentService.value.key as ServiceType

// School (for name + is_production) — read from the list we already fetch.
const { data: schools } = useSchools(service)
const school = computed(() =>
  (schools.value ?? []).find((s: any) => s.id === props.id),
)
const isProduction = computed(() => !!school.value?.is_production)

// ---- Color-theme library ----
const { data: themes } = useColorThemes(service)
const { mutateAsync: createTheme, isPending: creating } = useCreateColorTheme(service)
const { mutateAsync: updateTheme, isPending: updating } = useUpdateColorTheme(service)
// Assign a library theme to the school BY REFERENCE: sets schools.color_theme_id
// to the selected theme's id (no copy of colors).
const { mutateAsync: assignTheme, isPending: assigning } = useAssignSchoolColorTheme(service)

const themeOptions = computed(() =>
  (themes.value ?? []).map((t: any) => ({ value: t.id, text: t.name })),
)
// Seed from the school's current color_theme_id (reference) once loaded.
const selectedThemeId = ref<string>('')
watch(
  school,
  (s: any) => {
    if (s?.color_theme_id && !selectedThemeId.value) selectedThemeId.value = s.color_theme_id
  },
  { immediate: true },
)
const selectedTheme = computed(() =>
  (themes.value ?? []).find((t: any) => t.id === selectedThemeId.value),
)

async function handleAssignTheme() {
  if (!selectedThemeId.value) return
  await assignTheme({ schoolId: props.id, colorThemeId: selectedThemeId.value })
}

// Create / edit theme modal
const editorOpen = ref(false)
const editorMode = ref<'create' | 'edit'>('create')
const editorInitial = ref<Partial<SchoolColorThemeData> | undefined>(undefined)

function openCreate() {
  editorMode.value = 'create'
  editorInitial.value = undefined
  editorOpen.value = true
}
function openEdit() {
  if (!selectedTheme.value) return
  editorMode.value = 'edit'
  editorInitial.value = { ...selectedTheme.value }
  editorOpen.value = true
}
async function handleEditorSubmit(data: SchoolColorThemeData) {
  if (editorMode.value === 'create') {
    const created: any = await createTheme(data)
    if (created?.id) selectedThemeId.value = created.id
  } else if (selectedThemeId.value) {
    await updateTheme({ id: selectedThemeId.value, body: data })
  }
  editorOpen.value = false
}

// ---- Logo ----
const { data: logoUrl } = useSchoolLogo(service, props.id)
const { mutateAsync: uploadLogo, isPending: logoSaving } = useUploadSchoolLogo(service)
const logoFile = ref<File | null>(null)
const logoPreviewUrl = computed(() => logoUrl.value ?? null)

async function handleSaveLogo() {
  if (!logoFile.value) return
  await uploadLogo({ schoolId: props.id, file: logoFile.value })
  logoFile.value = null
}

function handleCopy() {
  router.push({ name: 'schools-copy', params: { id: props.id } })
}
function handleBack() {
  router.push({ name: 'schools' })
}
</script>

<template>
  <AdminLayout>
    <div class="school-detail">
      <div class="school-detail__header">
        <div class="school-detail__header-left">
          <Button variant="ghost" size="sm" @click="handleBack">
            <ArrowLeft :size="16" />
            Back
          </Button>
          <h2 class="school-detail__heading">
            {{ school?.name ?? 'School Details' }}
          </h2>
          <span v-if="isProduction" class="school-detail__prod-badge" title="This school is used in production">
            Production
          </span>
          <code class="school-detail__id">{{ id }}</code>
        </div>
        <div class="school-detail__header-right">
          <Button variant="outline" @click="handleCopy">
            <Copy :size="16" />
            Copy School
          </Button>
        </div>
      </div>

      <p v-if="isProduction" class="school-detail__prod-note">
        This is a production school — changes here affect a live environment. Edit with care.
      </p>

      <div class="school-detail__grid">
        <Card class="school-detail__card">
          <template #default>
            <h3 class="school-detail__card-title">Color Theme</h3>
            <SelectInput
              label="Color scheme"
              :model-value="selectedThemeId"
              :options="themeOptions"
              placeholder="Select a theme"
              @update:model-value="selectedThemeId = $event"
            />
            <div class="school-detail__theme-buttons">
              <Button variant="outline" size="sm" @click="openCreate">
                <Plus :size="14" /> New theme
              </Button>
              <Button
                variant="outline"
                size="sm"
                :disabled="!selectedThemeId"
                @click="openEdit"
              >
                <Pencil :size="14" /> Edit theme
              </Button>
            </div>
            <div class="school-detail__card-actions">
              <Button :disabled="!selectedThemeId || assigning" @click="handleAssignTheme">
                {{ assigning ? 'Applying…' : 'Apply to school' }}
              </Button>
            </div>
          </template>
        </Card>

        <Card class="school-detail__card">
          <template #default>
            <h3 class="school-detail__card-title">School Logo</h3>
            <LogoUploader v-model="logoFile" :preview-url="logoPreviewUrl" />
            <div class="school-detail__card-actions">
              <Button :disabled="!logoFile || logoSaving" @click="handleSaveLogo">
                {{ logoSaving ? 'Uploading...' : 'Upload Logo' }}
              </Button>
            </div>
          </template>
        </Card>
      </div>

      <Modal
        :open="editorOpen"
        size="lg"
        :title="editorMode === 'create' ? 'Create color theme' : 'Edit color theme'"
        @close="editorOpen = false"
      >
        <ColorThemeEditor
          :initial="editorInitial"
          :saving="creating || updating"
          :submit-label="editorMode === 'create' ? 'Create theme' : 'Save changes'"
          @submit="handleEditorSubmit"
          @cancel="editorOpen = false"
        />
      </Modal>
    </div>
  </AdminLayout>
</template>

<style scoped>
.school-detail {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.school-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.school-detail__header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.school-detail__heading {
  font-size: 20px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
}
.school-detail__prod-badge {
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  padding: 2px 10px;
  border-radius: 999px;
}
.school-detail__prod-note {
  margin: 0;
  font-size: 13px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
}
.school-detail__id {
  font-size: 13px;
  background: #f5f5f4;
  padding: 2px 8px;
  border-radius: 4px;
  color: #78716c;
}
.school-detail__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
@media (max-width: 768px) {
  .school-detail__grid {
    grid-template-columns: 1fr;
  }
}
.school-detail__card {
  display: flex;
  flex-direction: column;
}
.school-detail__card > :deep(*) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}
.school-detail__card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
}
.school-detail__theme-buttons {
  display: flex;
  gap: 8px;
}
.school-detail__card-actions {
  padding-top: 8px;
  border-top: 1px solid #e7e5e4;
  display: flex;
  justify-content: flex-end;
}
</style>
